import { App } from 'antd';
import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';

/**
 * Предупреждение о несохранённых изменениях (экран 3): при закрытии вкладки — браузерное,
 * при переходе по ссылке внутри кабинета — окно «Уйти без сохранения?».
 * (BrowserRouter не умеет блокировать переходы — перехватываем клики по ссылкам и «Назад».)
 */
export function useUnsavedGuard(
  dirty: boolean,
  text = 'Изменения в настройке ассистента не сохранены.',
) {
  const { modal } = App.useApp();
  const navigate = useNavigate();

  useEffect(() => {
    if (!dirty) return undefined;
    const onUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey) return;
      const a = (e.target as HTMLElement | null)?.closest('a');
      const href = a?.getAttribute('href');
      if (!a || !href || !href.startsWith('/') || a.target === '_blank') return;
      if (href === window.location.pathname) return;
      e.preventDefault();
      e.stopPropagation();
      modal.confirm({
        title: 'Уйти без сохранения?',
        content: text,
        okText: 'Уйти',
        cancelText: 'Остаться',
        // replace — вместо служебной записи истории (см. ниже), иначе «Назад» потом нажимать дважды.
        onOk: () => navigate(href, { replace: true }),
      });
    };
    // Кнопка «Назад» браузера: держим копию текущей записи истории сверху; «Назад»
    // снимает её — остаёмся на странице и спрашиваем. «Уйти» — шаг назад по-настоящему.
    let leaving = false;
    const here = window.location.href;
    const mark = (st: unknown) => ({ ...(st as object | null), richatGuard: true });
    window.history.pushState(mark(window.history.state), '', here);
    const onPop = () => {
      if (leaving) return;
      window.history.pushState(mark(window.history.state), '', here);
      modal.confirm({
        title: 'Уйти без сохранения?',
        content: text,
        okText: 'Уйти',
        cancelText: 'Остаться',
        onOk: () => {
          leaving = true;
          window.history.go(-2);
        },
      });
    };
    window.addEventListener('beforeunload', onUnload);
    window.addEventListener('popstate', onPop);
    document.addEventListener('click', onClick, true);
    return () => {
      window.removeEventListener('beforeunload', onUnload);
      window.removeEventListener('popstate', onPop);
      document.removeEventListener('click', onClick, true);
      // Изменения сохранили (страница та же) — убираем служебную запись истории.
      const st = window.history.state as { richatGuard?: boolean } | null;
      if (!leaving && st?.richatGuard && window.location.href === here) window.history.back();
    };
  }, [dirty, modal, navigate, text]);
}
